/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, MapPin, Clock, Star, Award, PlayCircle, CheckCircle2, ArrowRight, Sparkles, Briefcase, GraduationCap, Users } from "lucide-react";
import UrgencyBar from "@/components/UrgencyBar";
import FreeSyllabusCTA from "@/components/FreeSyllabusCTA";
import ShareButton from "@/components/ShareButton";
import { getCoursePricing, formatINR } from "@/lib/pricing";

const fontStack = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };
const WHATSAPP_BASE = "https://wa.me/919131341638?text=";

/** SEO-friendly city landing page e.g. /courses/digital-marketing-master-indore */
export default function CityCoursePage() {
  const params = useParams();
  const slugCity = params.slugCity || params.id;
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [city, setCity] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setCourse(null); setCity(null); setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/courses/locale/${slugCity}`)
      .then(({ data }) => { setCourse(data.course); setCity(data.city); })
      .catch(() => setNotFound(true));
  }, [slugCity]);

  // Dynamic SEO tags
  useEffect(() => {
    if (!course || !city) return;
    const title = `${course.title} in ${city.name} — Live Practical Training | CodeWithKamlesh`;
    const desc = `${course.title} in ${city.name}, ${city.state}. Live online + offline classes, real projects, placement support and industry certification. ${course.tagline || ""}`.slice(0, 158);
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, true);
    setMeta("og:description", desc, true);
    if (course.image_url) {
      const og = course.image_url.startsWith("http") ? course.image_url : window.location.origin + course.image_url;
      setMeta("og:image", og, true);
    }
    setCanonical(window.location.href);
    setJsonLd(course, city);
    return () => { setJsonLd(null); };
  }, [course, city]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-[#050505] text-white flex items-center justify-center p-6" style={fontStack}>
        <div className="max-w-md w-full bg-[#121212] border border-white/10 rounded-2xl p-8 text-center">
          <h1 className="text-2xl font-bold" style={displayStack}>Page not found</h1>
          <p className="text-sm text-white/60 mt-2">The course / city combination doesn't exist.</p>
          <Link to="/courses" className="mt-5 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white text-sm font-semibold rounded-full px-5 py-2.5 transition">View all courses</Link>
        </div>
      </div>
    );
  }
  if (!course || !city) {
    return (
      <div className="bg-[#050505] text-white min-h-screen flex items-center justify-center" style={fontStack}>
        <div className="animate-pulse text-white/40">Loading…</div>
      </div>
    );
  }

  const { mrp, final: price, hasDiscount, percentOff } = getCoursePricing(course);
  const waText = `Hi%20Sir%2C%20I%20want%20to%20enroll%20in%20${encodeURIComponent(course.title)}%20in%20${encodeURIComponent(city.name)}`;

  return (
    <div className="bg-[#050505] text-white min-h-screen pb-32 md:pb-0" style={fontStack} data-testid="city-course-page">
      <UrgencyBar nextBatchDate={course.next_batch_date} seatsLeft={course.seats_left ?? 7} seatsTotal={course.seats_total ?? 20} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF0066]/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6" data-testid="back-to-courses">
            <ArrowLeft className="w-4 h-4" /> All courses
          </Link>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              {/* Breadcrumb */}
              <nav className="text-[11px] text-white/40 mb-4" aria-label="breadcrumb">
                <Link to="/" className="hover:text-white">Home</Link> <span className="mx-1">/</span>
                <Link to="/courses" className="hover:text-white">Courses</Link> <span className="mx-1">/</span>
                <span className="text-white/60">{course.title}</span> <span className="mx-1">in</span>
                <span className="text-[#FF0066]">{city.name}</span>
              </nav>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-400/25 text-xs font-medium text-emerald-300">
                  <MapPin className="w-3 h-3" /> {city.name}, {city.state}
                </span>
                {course.badge && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FF0066]/15 border border-[#FF0066]/30 text-xs font-bold text-[#FF0066] uppercase tracking-wider" data-testid="city-course-badge">
                    <Sparkles className="w-3 h-3" /> {course.badge}
                  </span>
                )}
                {course.level && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-white/80">{course.level}</span>
                )}
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight" style={displayStack} data-testid="city-course-title">
                {course.title} in <span className="text-[#FF0066]">{city.name}</span>
              </h1>
              {course.tagline && <p className="mt-3 text-base md:text-lg text-[#FF0066]/90 font-medium leading-snug max-w-2xl">{course.tagline}</p>}
              <p className="mt-4 text-sm md:text-base text-white/75 leading-relaxed max-w-2xl">
                Join the most practical <b>{course.title}</b> from <b>{city.name}, {city.state}</b>. Live online classes (join from anywhere in {city.name}),
                weekend offline sessions in our Indore centre, real client projects, industry certification and placement support.
                {course.description ? ` ${course.description}` : ""}
              </p>

              {course.outcome && (
                <div className="mt-5 inline-flex items-start gap-2.5 bg-emerald-500/10 border border-emerald-400/25 rounded-lg px-4 py-2.5 max-w-2xl">
                  <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Outcome</div>
                    <div className="text-sm text-white/85 leading-snug">{course.outcome}</div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/70">
                <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#FF0066]" /> {course.duration_weeks || 12} weeks</span>
                <span className="inline-flex items-center gap-1.5"><PlayCircle className="w-4 h-4 text-[#FF0066]" /> Live online + Indore centre</span>
                <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400 fill-current" /> {course.rating || 4.8}</span>
                <span className="inline-flex items-center gap-1.5"><Award className="w-4 h-4 text-[#FF0066]" /> Certificate</span>
              </div>

              <div className="mt-5">
                <FreeSyllabusCTA courseId={course.id} courseTitle={`${course.title} (${city.name})`} />
              </div>

              {/* Why join from this city */}
              <div className="mt-8 bg-[#121212] border border-white/10 rounded-xl p-5 max-w-3xl">
                <div className="text-xs uppercase tracking-wider text-[#FF0066] font-bold mb-2">Why students from {city.name} choose us</div>
                <ul className="space-y-2 text-sm text-white/80">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" /> 100% live classes — join from any city in {city.state}, no relocation needed.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" /> Lifetime WhatsApp + Telegram alumni group with peers from {city.name} and across India.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" /> Placement support — agency hiring network, freelance projects, internship referrals.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" /> Visit our Indore centre anytime (Apollo Square) for in-person doubt sessions.</li>
                </ul>
              </div>
            </div>

            {/* Sticky Purchase Card */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 bg-[#121212] border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-[#FF0066]/10">
                {course.image_url && (
                  <div className="relative aspect-[16/10] bg-[#0a0a0a] border-b border-white/10">
                    <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-3xl font-extrabold text-white" style={displayStack} data-testid={`city-price-${course.id}`}>{formatINR(price, { showFree: false })}</span>
                    {hasDiscount && (
                      <>
                        <span className="text-sm text-white/40 line-through" data-testid={`city-mrp-${course.id}`}>{formatINR(mrp, { showFree: false })}</span>
                        <span className="text-xs text-emerald-300 font-semibold">{percentOff}% off</span>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => nav(`/courses/${course.slug || course.id}`)}
                    className="mt-5 w-full inline-flex items-center justify-center bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-4 py-3.5 text-sm transition shadow-[0_8px_30px_-8px_rgba(255,0,102,0.6)]"
                    data-testid="city-enroll-btn"
                  >
                    Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                  <a href={`${WHATSAPP_BASE}${waText}`} target="_blank" rel="noreferrer"
                     className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium rounded-full px-4 py-2.5 text-sm transition"
                     data-testid="city-wa-btn">
                    Ask on WhatsApp
                  </a>
                  <div className="mt-3">
                    <ShareButton type="city" slug={slugCity} title={`${course.title} in ${city.name}`} />
                  </div>
                  <div className="mt-5 space-y-2 text-xs text-white/70">
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#FF0066]" /> Available in {city.name}, {city.state}</div>
                    <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-[#FF0066]" /> Small batch (max 25 students)</div>
                    <div className="flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#FF0066]" /> Government-recognised certificate</div>
                    <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-[#FF0066]" /> Placement support</div>
                    <div className="flex items-center gap-2"><GraduationCap className="w-3.5 h-3.5 text-[#FF0066]" /> 7-day money-back guarantee</div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Bottom CTA — link to full canonical course page */}
      <section className="py-14 md:py-16 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold" style={displayStack}>Ready to start in {city.name}?</h2>
          <p className="mt-2 text-sm text-white/65">View full curriculum, modules, FAQ and student stories on the main course page.</p>
          <Link to={`/courses/${course.slug || course.id}`} className="mt-5 inline-flex items-center gap-2 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-6 py-3 text-sm transition" data-testid="city-view-full-course">
            View full course details <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// ----- helpers -----
function setMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setJsonLd(course, city) {
  let el = document.getElementById("cwk-jsonld-city");
  if (!course) { if (el) el.remove(); return; }
  const data = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: `${course.title} in ${city.name}`,
    description: `${course.title} live training in ${city.name}, ${city.state}. Real projects, placement support, certificate.`,
    provider: {
      "@type": "Organization",
      name: "CodeWithKamlesh",
      sameAs: "https://codewithkamlesh.in",
    },
    offers: {
      "@type": "Offer",
      price: getCoursePricing(course).final,
      priceCurrency: "INR",
      url: typeof window !== "undefined" ? window.location.href : "",
    },
    educationalLevel: course.level || "Beginner to Advanced",
    timeRequired: `PT${(course.duration_weeks || 12) * 7 * 24}H`,
    inLanguage: "en-IN",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: course.rating || 4.8,
      reviewCount: course.enrolled_count || 50,
    },
    areaServed: { "@type": "City", name: city.name, addressRegion: city.state, addressCountry: "IN" },
  };
  if (!el) {
    el = document.createElement("script");
    el.id = "cwk-jsonld-city";
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
